import { Box, Divider } from '@mui/material';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isFinished, isLoading, Analytics } from '@/shared/lib';
import { SortOptions } from '@/shared/types';
import { Checkbox, Button, SearchInput, SortButton } from '@/shared/ui';
import { getStringFilterOptionValue } from '../../libs/getStringFilterOptionValue';
import type { StringFilterParams } from '../../model/types';
import { StringFilterSelectionType } from '../../model/types';
import type { ContainsValue } from '../ContainsStringFilterCortrol/ContainsStringFilterCortrol';
import {
  ContainsStringFilterCortrol,
  ContainsOperator,
} from '../ContainsStringFilterCortrol/ContainsStringFilterCortrol';
import {
  FilterSelectionControlsProvider,
  useFilterSelectionControlsContext,
} from '../FilterSelectionControlsContext/FilterSelectionControlsContext';
import { FilterSelectionTypeSelect } from '../FilterSelectionTypeSelect/FilterSelectionTypeSelect';
import { useFiltersParamsContext } from '../FiltersParamsContext/FiltersParamsContext';
import { SelectedCountLabel } from '../SelectedCountLabel/SelectedCountLabel';
import type { ItemRendererProps } from '../VirtualizedFilter/VirtualizedFilter';
import { VirtualizedFilter } from '../VirtualizedFilter/VirtualizedFilter';

interface IncludeValue {
  type: StringFilterSelectionType.INCLUDE;
  options: string[];
}

interface ExcludeValue {
  type: StringFilterSelectionType.EXCLUDE;
  options: string[];
}

type IncludeExcludeValue = IncludeValue | ExcludeValue;

export type Value = IncludeExcludeValue | ContainsValue;

interface Props {
  id: string;
  value?: Value;
  onChange: (v: Value) => void;
}

export function StringFilter(props: Props) {
  const { paramsData, requestStringFilterParams } = useFiltersParamsContext();
  const filterParamsLoadable = paramsData[props.id];

  useEffect(
    () => {
      if (
        isFinished(filterParamsLoadable?.status) ||
        isLoading(filterParamsLoadable?.status)
      ) {
        return;
      }

      requestStringFilterParams(props.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterParamsLoadable],
  );

  if (
    props.value?.type === StringFilterSelectionType.CONTAINS ||
    props.value?.type === StringFilterSelectionType.NOT_CONTAIN
  ) {
    return (
      <ContainsStringFilterCortrol
        {...props}
        getOptionValue={getStringFilterOptionValue}
        value={props.value as ContainsValue}
      />
    );
  }

  return (
    <FilterSelectionControlsProvider
      id={props.id}
      value={props.value?.options || []}
      getOptionValue={getStringFilterOptionValue}
    >
      <IncludeExcludeContent
        {...props}
        value={props.value as IncludeExcludeValue}
      />
    </FilterSelectionControlsProvider>
  );
}

interface IncludeExcludeProps {
  id: string;
  value?: IncludeExcludeValue;
  onChange: (v: Value) => void;
}

function IncludeExcludeContent({ id, value, onChange }: IncludeExcludeProps) {
  const {
    filterQuery,
    setFilterQuery,
    getSelectAllValue,
    getClearAllValue,
    isClearAllDisabled,
  } = useFilterSelectionControlsContext();
  const { paramsData } = useFiltersParamsContext();
  const [sortOrder, setSortOrder] = useState<SortOptions | undefined>();
  const optionsSortComparator = useMemo(() => {
    if (!sortOrder) {
      return undefined;
    }

    return sortOrder === SortOptions.ASC
      ? (a: string, b: string) => a.localeCompare(b)
      : (a: string, b: string) => b.localeCompare(a);
  }, [sortOrder]);
  const filterParamsLoadable = paramsData[id];
  const allOptions = useMemo(
    () =>
      (filterParamsLoadable?.params as StringFilterParams | undefined)
        ?.options || [],
    [filterParamsLoadable],
  );
  const notPresentOptions = useMemo(() => {
    const availableOptions = new Set(allOptions);

    return (value?.options || []).filter((option) => {
      return !availableOptions.has(option);
    });
  }, [value, allOptions]);

  useEffect(
    () => {
      if (isFinished(filterParamsLoadable?.status) && !value) {
        onChange({
          type: StringFilterSelectionType.INCLUDE,
          options: allOptions,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterParamsLoadable],
  );

  const handleTypeChange = (type: StringFilterSelectionType) => {
    if (
      type === StringFilterSelectionType.CONTAINS ||
      type === StringFilterSelectionType.NOT_CONTAIN
    ) {
      onChange({
        type,
        matchingStrings: [],
        operator: ContainsOperator.AND,
      });
    } else {
      onChange({
        type,
        options: value?.options || allOptions,
      });
    }
    Analytics.sendFeatureUsage('string_filter', 'type_change', {
      type,
    });
  };
  const handleSortOrderChange = (newSortOrder?: SortOptions) => {
    setSortOrder(newSortOrder);
    Analytics.sendFeatureUsage('string_filter', 'sorting', {
      order: newSortOrder === undefined ? 'no_sorting' : newSortOrder,
    });
  };
  const handleOptionsChange = (options: string[]) => {
    onChange({
      type: value?.type || StringFilterSelectionType.INCLUDE,
      options,
    });
  };
  const handleClearAll = () => {
    if (!value) {
      return;
    }

    onChange({
      type: value.type,
      options: getClearAllValue(),
    });
  };
  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (!value) {
      return;
    }

    if (event.target.checked) {
      onChange({
        type: value.type,
        options: getSelectAllValue(),
      });
    } else {
      onChange({
        type: value.type,
        options: getClearAllValue(),
      });
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
        <FilterSelectionTypeSelect
          value={value?.type}
          onChange={handleTypeChange}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <SearchInput query={filterQuery} onChange={setFilterQuery} />
          <SortButton
            sx={{
              width: 40,
              border: '1px solid #BBBEC8',
              backgroundColor: sortOrder ? '#E7ECFA' : 'transparent',
            }}
            value={sortOrder}
            onChange={handleSortOrderChange}
          />
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <Checkbox
          sx={{ p: 0 }}
          size="small"
          indeterminate={
            value?.options &&
            value?.options.length > 0 &&
            value?.options.length < allOptions.length
          }
          checked={allOptions.length === value?.options.length}
          onChange={handleSelectAll}
        />
        <SelectedCountLabel
          notPresetCount={notPresentOptions.length}
          optionsCount={value?.options.length || 0}
          totalCount={allOptions.length}
        />
        <Button
          sx={{ p: 0, lineHeight: '1.375rem', color: 'black' }}
          disabled={isClearAllDisabled(value?.options || [])}
          variant="text"
          color="secondary"
          onClick={handleClearAll}
        >
          Clear all
        </Button>
      </Box>
      <Divider />
      <VirtualizedFilter
        ItemRenderer={StringOptionRenderer}
        value={value?.options}
        onChange={handleOptionsChange}
        optionsSortComparator={optionsSortComparator}
      />
    </div>
  );
}

function StringOptionRenderer({ value, setHeight }: ItemRendererProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      if (rootRef.current) {
        setHeight(rootRef.current.clientHeight);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootRef],
  );

  return (
    <Box ref={rootRef} component="span" sx={{ wordBreak: 'break-word' }}>
      {value as string}
    </Box>
  );
}
